import React, { useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  LayoutAnimation,
  Platform,
  UIManager,
} from "react-native";
import { Feather } from "@expo/vector-icons";
import { Venus, Mars, LayoutGrid, Baby } from "lucide-react-native";
import { CATEGORIES } from "../../lib/categories";
import { spacing, typography } from "../../theme";
import { useColors, ThemeColors } from "../../hooks/useColors";

// Enable LayoutAnimation for Android
if (
  Platform.OS === "android" &&
  UIManager.setLayoutAnimationEnabledExperimental
) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

interface CategoryBrowserProps {
  onSelectCategory: (
    categoryId: string,
    subcategoryId?: string,
    item?: string
  ) => void;
  expandedCategory: string | null;
  expandedSubcategory: string | null;
}

export function CategoryBrowser({
  onSelectCategory,
  expandedCategory,
  expandedSubcategory,
}: CategoryBrowserProps) {
  const colors = useColors();
  const styles = createStyles(colors);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    expandedCategory || null
  );
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(
    expandedSubcategory || null
  );

  // Update internal state when props change
  React.useEffect(() => {
    if (expandedCategory !== undefined) {
      setSelectedCategory(expandedCategory);
    }
    if (expandedSubcategory !== undefined) {
      setSelectedSubcategory(expandedSubcategory);
    }
  }, [expandedCategory, expandedSubcategory]);

  const handleCategoryPress = (categoryId: string) => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setSelectedCategory(categoryId === selectedCategory ? null : categoryId);
    setSelectedSubcategory(null);
  };

  const handleSubcategoryPress = (
    categoryId: string,
    subcategoryId: string
  ) => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    if (categoryId === "other") {
      setSelectedSubcategory(
        subcategoryId === selectedSubcategory ? null : subcategoryId
      );
      return;
    }
    setSelectedSubcategory(
      subcategoryId === selectedSubcategory ? null : subcategoryId
    );
  };

  const handleItemPress = (
    categoryId: string,
    subcategoryId: string,
    item: string
  ) => {
    if (categoryId === "other") return;
    onSelectCategory(categoryId, subcategoryId, item);
  };

  return (
    <ScrollView 
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      showsVerticalScrollIndicator={false}
    >
      {CATEGORIES.map((category) => (
        <View key={category.id} style={styles.categoryCard}>
          <TouchableOpacity
            activeOpacity={0.7}
            style={[
              styles.categoryHeader,
              selectedCategory === category.id && { backgroundColor: colors.primary },
              category.id === "other" && styles.comingSoonCategory,
            ]}
            onPress={() => handleCategoryPress(category.id)}
          >
            <View style={styles.categoryTitleContainer}>
              <View style={[
                styles.iconContainer,
                selectedCategory === category.id && { backgroundColor: "rgba(0,0,0,0.1)", borderColor: "rgba(0,0,0,0.1)" }
              ]}>
                {category.lucideIcon === "venus" ? (
                  <Venus
                    size={20}
                    color={selectedCategory === category.id ? "black" : colors.text}
                  />
                ) : category.lucideIcon === "mars" ? (
                  <Mars
                    size={20}
                    color={selectedCategory === category.id ? "black" : colors.text}
                  />
                ) : category.lucideIcon === "baby" ? (
                  <Baby
                    size={20}
                    color={selectedCategory === category.id ? "black" : colors.text}
                  />
                ) : category.lucideIcon === "layout-grid" ? (
                  <LayoutGrid
                    size={20}
                    color={selectedCategory === category.id ? "black" : colors.text}
                  />
                ) : (
                <Feather
                  name={category.icon as any}
                  size={20}
                  color={selectedCategory === category.id ? "black" : colors.text}
                />
                )}
              </View>
              <Text
                style={[
                  styles.categoryTitle,
                  selectedCategory === category.id && { color: "black" },
                  category.id === "other" && styles.comingSoonText,
                ]}
              >
                {category.name}
              </Text>
            </View>
            <Feather
              name={
                selectedCategory === category.id ? "chevron-up" : "chevron-down"
              }
              size={20}
              color={selectedCategory === category.id ? "black" : colors.textSecondary}
            />
          </TouchableOpacity>

          {selectedCategory === category.id && (
            <View style={styles.subcategoriesContainer}>
              {category.subcategories.map((subcategory) => (
                <View key={subcategory.id} style={styles.subcategoryWrapper}>
                  <TouchableOpacity
                    activeOpacity={0.6}
                    style={[
                      styles.subcategoryHeader,
                      selectedSubcategory === subcategory.id && styles.activeSubcategoryHeader,
                      category.id === "other" && styles.comingSoonSubcategory,
                    ]}
                    onPress={() =>
                      handleSubcategoryPress(category.id, subcategory.id)
                    }
                  >
                    <Text
                      style={[
                        styles.subcategoryTitle,
                        selectedSubcategory === subcategory.id && styles.activeSubcategoryTitle,
                        category.id === "other" && styles.comingSoonText,
                      ]}
                    >
                      {subcategory.name}
                    </Text>
                    {subcategory.items && (
                      <Feather
                        name={
                          selectedSubcategory === subcategory.id
                            ? "chevron-up"
                            : "chevron-down"
                        }
                        size={18}
                        color={selectedSubcategory === subcategory.id ? colors.primary : colors.textSecondary}
                      />
                    )}
                  </TouchableOpacity>

                  {selectedSubcategory === subcategory.id &&
                    subcategory.items && (
                      <View style={styles.itemsContainer}>
                        {subcategory.items.map((item, index) => (
                          <TouchableOpacity
                            key={index}
                            activeOpacity={0.5}
                            style={[
                              styles.itemButton,
                              category.id === "other" && styles.comingSoonItem,
                            ]}
                            onPress={() =>
                              handleItemPress(category.id, subcategory.id, item)
                            }
                            disabled={category.id === "other"}
                          >
                            <Text
                              style={[
                                styles.itemText,
                                category.id === "other" &&
                                  styles.comingSoonText,
                              ]}
                            >
                              {item}
                            </Text>
                            {category.id !== "other" && (
                              <Feather
                                name="arrow-right"
                                size={16}
                                color={colors.textLight}
                              />
                            )}
                          </TouchableOpacity>
                        ))}
                      </View>
                    )}
                </View>
              ))}
            </View>
          )}
        </View>
      ))}
    </ScrollView>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    contentContainer: {
      paddingHorizontal: spacing.lg,
      paddingTop: spacing.xs,
      paddingBottom: spacing.xl,
    },
    categoryCard: {
      backgroundColor: colors.surface,
      borderRadius: 20,
      marginBottom: spacing.md,
      overflow: "hidden",
      borderWidth: 1,
      borderColor: colors.border,
      // Subtle shadow
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.03,
      shadowRadius: 8,
      elevation: 2,
    },
    categoryHeader: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      padding: spacing.md,
      minHeight: 72,
    },
    categoryTitleContainer: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.md,
    },
    iconContainer: {
      width: 44,
      height: 44,
      borderRadius: 14,
      backgroundColor: colors.background,
      alignItems: "center",
      justifyContent: "center",
      borderWidth: 1,
      borderColor: colors.border,
    },
    categoryTitle: {
      fontSize: 17,
      fontWeight: "600",
      color: colors.text,
      letterSpacing: -0.2,
    },
    subcategoriesContainer: {
      backgroundColor: colors.background,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    subcategoryWrapper: {
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    subcategoryHeader: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.lg,
      minHeight: 56,
    },
    activeSubcategoryHeader: {
      backgroundColor: colors.surface,
    },
    subcategoryTitle: {
      fontSize: 15,
      fontWeight: "500",
      color: colors.text,
    },
    activeSubcategoryTitle: {
      color: colors.primary,
      fontWeight: "600",
    },
    itemsContainer: {
      paddingLeft: spacing.lg,
      backgroundColor: colors.surface,
    },
    itemButton: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.lg,
      minHeight: 52,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    itemText: {
      fontSize: 15,
      color: colors.textSecondary,
      fontWeight: "400",
    },
    comingSoonText: {
      color: colors.textLight,
    },
    comingSoonCategory: {
      opacity: 0.6,
    },
    comingSoonSubcategory: {
      opacity: 0.6,
    },
    comingSoonItem: {
      opacity: 0.6,
    },
  });
